import { podoFetcher } from '@server/infrastructure/external-apis/podo/client'

import { getBearerToken } from '../../authentication'
import type { SubscribeProtectionResponse, UnsubscribeRequest, UnsubscribeResponse } from '../model/churn.types'

type NormalizedOrLegacyResult<T> = {
  resultCd: number | string
  message?: string | null
  resultMsg?: string | null
  data?: T
  result?: T
}

const hasOwn = <K extends PropertyKey>(value: object, key: K): value is object & Record<K, unknown> =>
  Object.prototype.hasOwnProperty.call(value, key)

const getLegacyResultMsg = ({
  message,
  resultMsg,
}: Pick<NormalizedOrLegacyResult<unknown>, 'message' | 'resultMsg'>): string | undefined => {
  if (typeof message === 'string' && message.length > 0) {
    return message
  }

  if (resultMsg !== null && resultMsg !== undefined) {
    return resultMsg
  }

  return message === '' ? '' : undefined
}

const toLegacyResultResponse = <T>(
  response: NormalizedOrLegacyResult<T>,
): { resultCd: string; resultMsg?: string; result: T } => {
  const resultMsg = getLegacyResultMsg(response)

  return {
    resultCd: String(response.resultCd),
    ...(resultMsg !== undefined ? { resultMsg } : {}),
    result: (hasOwn(response, 'data') ? response.data : response.result) as T,
  }
}

const toLegacyStatusResponse = (response: {
  resultCd: number | string
  message?: string | null
  resultMsg?: string | null
}): UnsubscribeResponse => {
  const resultMsg = getLegacyResultMsg(response)

  return {
    resultCd: String(response.resultCd),
    ...(resultMsg !== undefined ? { resultMsg } : {}),
  }
}

/**
 * V1 API - Get subscription protection info
 * @param subscribeMappId - Subscription mapping ID
 * @param bearerToken - Optional bearer token
 */
export const getSubscribeProtection = async (
  subscribeMappId: string,
  bearerToken?: string,
): Promise<SubscribeProtectionResponse> => {
  const authorizationToken = bearerToken ?? (await getBearerToken())
  const response = await podoFetcher<SubscribeProtectionResponse>({
    url: `/api/v1/subscribe/podo/getSubscribeProtection?subscribeMappId=${subscribeMappId}`,
    method: 'GET',
    header: {
      Authorization: `Bearer ${authorizationToken}`,
    },
  })

  return toLegacyResultResponse(response)
}

/**
 * Unsubscribe from a subscription
 * @param request - Unsubscribe request payload
 * @param bearerToken - Optional bearer token
 */
export const unsubscribe = async (request: UnsubscribeRequest, bearerToken?: string): Promise<UnsubscribeResponse> => {
  const authorizationToken = bearerToken ?? (await getBearerToken())
  const response = await podoFetcher<UnsubscribeResponse>({
    url: '/api/v1/subscribe/podo/unsubscribe',
    method: 'POST',
    header: {
      Authorization: `Bearer ${authorizationToken}`,
    },
    body: request,
  })

  return toLegacyStatusResponse(response)
}
